import React, {memo, useCallback, useEffect, useMemo, useState, useRef} from 'react'
import {Handle, NodeProps, Position, useNodes, useReactFlow, type Node, type Edge} from 'reactflow'
import {useDispatch, useSelector} from 'react-redux'
import {
  getAllTemplateWithoutPagination,
  getTemplateListWithoutPagination,
} from '../../../../MyTemplate/CreateScratch/CreateScratchSlices/CreateScratchSlices'
import Select, {MenuListProps, components} from 'react-select'
import {confirm} from 'react-confirm-box'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {CHANNELS} from '../../../../../../Utils/Constant/FlowConstants'
import {Modal} from 'react-bootstrap'
import CreateScratch from '../../../../MyTemplate/CreateScratch/CreateScratch'
import {TemplateMessage} from '../../../../MyTemplate/CreateScratch/Template'
import {DataNode, MetaDataWithSMS} from '../../FlowTypes'
import {useFlowNodeDelete} from '../hooks/useFlowNodeDelete'
import CreatableSelect from 'react-select/creatable'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'
import CreateScratchV2 from '../../../../MyTemplate/CreateScratch/CreateScratchV2'

const senderTypeData = [
  {name: '#SharedNum#', label: '#SharedNum#'},
  {name: 'dedicatedNumber', label: 'Dedicated Number'},
  {name: 'businessName', label: 'Business Name'},
  {name: 'customNumber', label: 'Custom Number'},
]
type ReactSelectOption = {
  value: string
  label: string
}

export const validateSMSNode = (data: DataNode<MetaDataWithSMS>, templates?: TemplateMessage[]) => {
  if (!data?.metaData?.sms) return 'Required message template'

  const selectedTemplate = data?.metaData?.sms

  if (selectedTemplate && templates && !templates.some((tpl) => tpl._id === selectedTemplate._id)) {
    return 'Your previously selected template has been deleted. Please choose a new one.'
  }

  return null
}

export interface SMSNodeProps extends NodeProps {
  pushToUndoStack?: (nodes: Node[], edges: Edge[]) => void
  isUndoRedoOperation?: React.MutableRefObject<boolean>
}

const SMSNode: React.FC<SMSNodeProps> = ({
  id,
  selected,
  data,
  pushToUndoStack,
  isUndoRedoOperation,
}) => {
  const dispatch = useDispatch()
  const {setNodes, setEdges, getEdges} = useReactFlow()
  const nodes = useNodes()
  const edges = getEdges()
  const templates: TemplateMessage[] = useSelector(getTemplateListWithoutPagination)
  const {t} = useI18n()

  const [selectedTemplate, setSelectedTemplate] = useState<TemplateMessage | null>(null)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false)
  const selectMenuPortalRef = useRef<HTMLDivElement | null>(null)
  const wheelHandler = useCallback((e: WheelEvent) => {
    const target = e.target as HTMLElement
    const selectMenu = target.closest('.react-select__menu')
    const smsNode = target.closest('.sms-node')

    if (selectMenu && smsNode) {
      e.stopPropagation()
    }
  }, [])

  useEffect(() => {
    return () => {
      if (selectMenuPortalRef.current) {
        selectMenuPortalRef.current.removeEventListener('wheel', wheelHandler)
        selectMenuPortalRef.current = null
      }

      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''

      const portals = document.querySelectorAll('.react-select__menu-portal')
      portals.forEach((portal) => {
        try {
          portal.remove()
        } catch (e) {}
      })
    }
  }, [wheelHandler])

  const [isEdit, setIsEdit] = useState(false)
  const [hovered, setHovered] = useState(false)

  const openCreateModal = useCallback(() => {
    setShowTemplateModal(true)
    setIsEdit(false)
  }, [])

  const MenuList = useCallback(
    (props: MenuListProps<any>) => {
      return (
        <components.MenuList {...props}>
          {props.children}
          <div
            style={{
              width: '100%',
              display: 'flex',
              gap: '6px',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              borderTop: '1px solid #F4F5F7',
              paddingTop: '10px',
            }}
            onClick={() => {
              openCreateModal()
            }}
          >
            <p
              style={{
                color: '#7950F2',
                margin: 0,
                fontWeight: 600,
              }}
            >
              + Create new
            </p>
          </div>
        </components.MenuList>
      )
    },
    [openCreateModal]
  )

  const node = useMemo(
    () => nodes.find((n) => n.id === id) as Node<DataNode<MetaDataWithSMS>>,
    [nodes, id]
  )

  useEffect(() => {
    if (isUpdatingTemplate) return

    const validationError = validateSMSNode(node?.data as DataNode<MetaDataWithSMS>, templates)

    const currentTemplate = node?.data?.metaData?.sms
    if (currentTemplate && templates && templates.length > 0) {
      const templateExists = templates.some((tpl) => tpl._id === currentTemplate._id)
      if (!templateExists) {
        if (selectedTemplate && selectedTemplate._id === currentTemplate._id) {
          setSelectedTemplate(null)
        }
      }
    }

    setLocalError(validationError)
  }, [selectedTemplate, node?.data?.metaData?.sms, templates, isUpdatingTemplate, node?.data])

  useEffect(() => {
    if (isUpdatingTemplate) return

    if (localError) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node?.id
            ? {
                ...n,
                data: {
                  ...n.data,
                  error: localError,
                },
              }
            : n
        )
      )
    } else {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node?.id
            ? {
                ...n,
                data: {
                  ...n.data,
                  error: null,
                },
              }
            : n
        )
      )
    }
  }, [localError, setNodes, node?.id, isUpdatingTemplate])

  const smsItem = useMemo(() => CHANNELS.find((item) => item.type === 'SMS'), [])

  const templateOptions = useMemo(
    () =>
      templates
        ?.filter((template: TemplateMessage) => template?.type?.toUpperCase() === 'SMS')
        ?.map((template: TemplateMessage) => ({
          value: template?.name,
          label: template?.name,
        })) || [],
    [templates]
  )

  const selectedOption = useMemo(() => {
    if (!selectedTemplate) return null

    const templateName =
      typeof selectedTemplate === 'object' && 'name' in selectedTemplate
        ? selectedTemplate.name
        : selectedTemplate

    return templateOptions.find((tpl) => tpl.value === templateName) || null
  }, [selectedTemplate, templateOptions])

  useEffect(() => {
    const smsFromNode = node?.data?.metaData?.sms || null
    setSelectedTemplate((prev) => {
      if (prev === smsFromNode || (prev && smsFromNode && prev.name === smsFromNode.name)) {
        return prev
      }
      return smsFromNode
    })
  }, [node?.data?.metaData?.sms])

  useEffect(() => {
    dispatch(getAllTemplateWithoutPagination())
  }, [dispatch])

  useEffect(() => {
    if (isSelectOpen) {
      const interval = setInterval(() => {
        const portal = document.querySelector('.react-select__menu-portal') as HTMLDivElement | null
        if (portal) {
          selectMenuPortalRef.current = portal
          portal.addEventListener('wheel', wheelHandler, {passive: true})
          clearInterval(interval)
        }
      }, 10)
      return () => {
        clearInterval(interval)
        if (selectMenuPortalRef.current) {
          selectMenuPortalRef.current.removeEventListener('wheel', wheelHandler)
          selectMenuPortalRef.current = null
        }
      }
    } else {
      if (selectMenuPortalRef.current) {
        selectMenuPortalRef.current.removeEventListener('wheel', wheelHandler)
        selectMenuPortalRef.current = null
      }
    }
  }, [isSelectOpen, wheelHandler])

  const confirmationOptions = useMemo(
    () => ({
      closeOnOverlayClick: false,
      labels: {
        confirmable: 'Yes',
        cancellable: 'No',
      },
    }),
    []
  )

  const handleTemplateSelection = useCallback(
    async (event: ReactSelectOption) => {
      const confirmation = await confirm(
        `Are you sure you want to use this ${event?.value} template`,
        confirmationOptions
      )

      if (!confirmation || !event?.value) return

      const template = templates?.find((key: TemplateMessage) => key?.name === event?.value)
      if (template) {
        setIsUpdatingTemplate(true)
        setSelectedTemplate(template)
        setLocalError(null)

        setNodes((nds) =>
          nds.map((n) =>
            n.id === node?.id
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    metaData: {
                      ...n.data?.metaData,
                      sms: template,
                    },
                    error: null,
                  },
                }
              : n
          )
        )
        setTimeout(() => setIsUpdatingTemplate(false), 100)
      }
    },
    [templates, confirmationOptions, setNodes, node?.id]
  )

  const handleModalSave = useCallback(
    (newTemplate?: TemplateMessage) => {
      setShowTemplateModal(false)
      dispatch(getAllTemplateWithoutPagination())

      if (newTemplate) {
        setIsUpdatingTemplate(true)
        setSelectedTemplate(newTemplate)
        setLocalError(null)

        setNodes((nds) =>
          nds.map((n) =>
            n.id === node?.id
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    metaData: {
                      ...n.data?.metaData,
                      sms: newTemplate,
                    },
                    error: null,
                  },
                }
              : n
          )
        )
        setTimeout(() => setIsUpdatingTemplate(false), 100)
      } else if (!isEdit && !selectedTemplate) {
        setTimeout(() => {
          const latestTemplates = templates.filter((tpl) => tpl.type?.toUpperCase() === 'SMS')
          if (latestTemplates.length > 0) {
            const latestTemplate = latestTemplates.reduce((latest, current) => {
              const latestTime = new Date(latest.createdAt || 0).getTime()
              const currentTime = new Date(current.createdAt || 0).getTime()
              return currentTime > latestTime ? current : latest
            })

            setIsUpdatingTemplate(true)
            setSelectedTemplate(latestTemplate)
            setLocalError(null)

            setNodes((nds) =>
              nds.map((n) =>
                n.id === node?.id
                  ? {
                      ...n,
                      data: {
                        ...n.data,
                        metaData: {
                          ...n.data?.metaData,
                          sms: latestTemplate,
                        },
                        error: null,
                      },
                    }
                  : n
              )
            )
            setTimeout(() => setIsUpdatingTemplate(false), 100)
          }
        }, 500)
      } else if (isEdit && !newTemplate) {
        setIsUpdatingTemplate(true)
        setSelectedTemplate(null)
        setNodes((nds) =>
          nds.map((n) =>
            n.id === node?.id
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    metaData: {
                      ...n.data?.metaData,
                      sms: undefined,
                    },
                  },
                }
              : n
          )
        )
        setTimeout(() => setIsUpdatingTemplate(false), 100)
      }
    },
    [dispatch, setNodes, node?.id, isEdit, selectedTemplate, templates]
  )

  const openEditModal = useCallback(() => {
    if (selectedTemplate) {
      setIsEdit(true)
      setShowTemplateModal(true)
    }
  }, [selectedTemplate])

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const error = localError || data?.error
  const hasError = !!error

  const {handleDelete} = useFlowNodeDelete({
    nodes,
    edges,
    setNodes,
    setEdges,
    pushToUndoStack,
    isUndoRedoOperation,
  })

  const renderSenderType = (senderType: string) => {
    return senderTypeData.find((type) => type.name === senderType)?.label || senderType
  }

  return (
    <div
      className={`sms-node shadow-sm sms-node-hover${selected ? ' border-primary selected' : ''}${
        hasError ? ' border-danger' : ''
      } nopan${hovered ? ' node-hover' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={hasError ? `Error: ${error}` : t('FLOW.NODE.SMS_DESC')}
      style={{
        borderWidth: hasError ? '2px' : '1px',
        borderStyle: 'solid',
        borderColor: hasError
          ? 'var(--bs-danger)'
          : selected
          ? 'var(--bs-primary)'
          : 'var(--bs-gray-300)',
      }}
    >
      <Handle
        type='target'
        position={Position.Top}
        id='sms-top'
        style={{
          background: hasError ? '#dc3545' : '#1976d2',
        }}
      />

      <div className='d-flex flex-column gap-2'>
        <div className='d-flex align-items-center gap-2 w-100'>
          <div
            className='automation-icon-container'
            style={smsItem?.bgColor ? {backgroundColor: smsItem.bgColor} : {}}
          >
            <img
              src={toAbsoluteUrl(smsItem?.svg || '')}
              alt={smsItem?.icon || 'sms-icon'}
              className='automation-icon'
            />
          </div>
          <div className='d-flex flex-column'>
            <h4
              className='sms-node-title'
              title={hasError ? `Error: ${error}` : t('FLOW.NODE.SMS_DESC')}
            >
              SMS
            </h4>
            {hasError && (
              <small className='text-danger fw-bold'>
                {error?.includes('deleted') ? '⚠ Template Deleted' : '⚠ Missing Template'}
              </small>
            )}
          </div>
          <div
            className='sms-node-action ms-auto d-flex align-items-center'
            style={{gap: '8px', position: 'relative'}}
          >
            {node?.data && (node.data as Node)?.deletable !== false && (
              <button
                className='btn btn-link p-0'
                style={{zIndex: 10, fontSize: '1.25rem', marginLeft: '4px'}}
                title='Delete Node'
                onClick={(e) => handleDelete(id, node, e)}
              >
                <i className='bi bi-trash text-danger fs-5' />
              </button>
            )}
          </div>
        </div>

        <div className='sms-node-select' onClick={stopPropagation}>
          <div className='d-flex align-items-center' style={{gap: 8}}>
            <p className='mb-2'>
              Sender ID: <strong>{node?.data?.metaData?.sender}</strong>
              <span
                className='text-muted ms-2'
                style={{cursor: 'help'}}
                title={`Sender Type: ${renderSenderType(node?.data?.metaData?.senderType ?? '')}`}
              >
                <i className='bi bi-info-circle' />
              </span>
            </p>
          </div>

          <div className='d-flex align-items-center' style={{gap: 8}}>
            <CreatableSelect
              isSearchable={true}
              value={selectedOption}
              onChange={(newValue) => {
                if (newValue) {
                  handleTemplateSelection(newValue)
                }
              }}
              options={templateOptions}
              name='message'
              className='common-select z-1000 w-100'
              placeholder='Templates'
              classNamePrefix='nodrag'
              menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              closeMenuOnScroll={true}
              menuShouldBlockScroll={false}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'var(--bs-gray-200) !important',
                  borderColor: error ? 'var(--bs-danger)' : base.borderColor,
                  boxShadow: error ? 'shadow-sm' : base.boxShadow,
                }),
              }}
              onMenuOpen={() => setIsSelectOpen(true)}
              onMenuClose={() => setIsSelectOpen(false)}
              openMenuOnFocus={true}
              components={{
                MenuList: (props) => <MenuList {...props} />,
              }}
            />
            {selectedTemplate && (
              <div className='d-flex align-items-center justify-content-center w-20'>
                <i
                  className='bi bi-pencil'
                  title='Create/Edit SMS Template'
                  onClick={openEditModal}
                />
              </div>
            )}
          </div>
          {error && (
            <div style={{color: 'var(--bs-danger)', fontSize: 12, marginTop: 2}}>{error}</div>
          )}
        </div>
      </div>

      <Handle
        type='source'
        position={Position.Bottom}
        id='sms-bottom'
        className='sms-node-handle'
      />

      <Modal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        size='xl'
        centered
        keyboard={true}
      >
        <Modal.Body>
          <div
            className='sms-node-modal-body'
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
          >
            <CreateScratchV2
              onSave={handleModalSave}
              shouldDirectAfterSave={false}
              isEdit={isEdit}
              id={isEdit ? selectedTemplate?._id : undefined}
              templateData={isEdit ? selectedTemplate : undefined}
            />
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default memo(SMSNode)
